import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Eye, MousePointerClick, TicketPercent, ChevronRight, RefreshCw, Loader2 } from "lucide-react";
import { BoostButton } from "@/components/promo/FlashCreditsWidgets";
import { EndingSoonBadge } from "@/components/posts/CountdownStrip";
import { useCountdown } from "@/hooks/useCountdown";

const STATUS_TONE = {
    PUBLISHED: "bg-emerald-100 text-emerald-700 border-emerald-200",
    DRAFT: "bg-neutral-100 text-neutral-700 border-neutral-200",
    EXPIRED: "bg-rose-100 text-rose-700 border-rose-200",
    ARCHIVED: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function MerchantOffers() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("ALL");

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams({ type: "OFFER", limit: "50" });
                const res = await api.get(`/me/posts?${params.toString()}`);
                setPosts(res.data);
            } catch (e) {
                toast.error("Could not load offers");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        if (tab === "ALL") return posts;
        return posts.filter((p) => p.status === tab);
    }, [posts, tab]);

    return (
        <MerchantLayout>
            <div className="px-4 pb-6 pt-4" data-testid="merchant-offers-page">
                <div className="mb-3 flex items-end justify-between">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                            Your posts
                        </div>
                        <h1 className="text-2xl font-extrabold text-neutral-900">Offers</h1>
                    </div>
                    <Link to="/merchant/offers/new">
                        <Button
                            className="nest-gradient text-white"
                            data-testid="offers-new-btn"
                        >
                            <Plus size={16} className="mr-1" /> New
                        </Button>
                    </Link>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="mt-2">
                    <TabsList className="grid w-full grid-cols-4" data-testid="offers-filter-tabs">
                        <TabsTrigger value="ALL" className="text-xs">All</TabsTrigger>
                        <TabsTrigger value="PUBLISHED" className="text-xs">Live</TabsTrigger>
                        <TabsTrigger value="DRAFT" className="text-xs">Draft</TabsTrigger>
                        <TabsTrigger value="EXPIRED" className="text-xs">Expired</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="mt-4 space-y-3">
                    {loading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-24 rounded-2xl" />
                        ))
                    ) : filtered.length ? (
                        filtered.map((p) => (
                            <OfferRow
                                key={p.id}
                                post={p}
                                onRenewed={(updated) =>
                                    setPosts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
                                }
                            />
                        ))
                    ) : (
                        <EmptyOffers />
                    )}
                </div>
            </div>
        </MerchantLayout>
    );
}

function OfferRow({ post, onRenewed }) {
    const a = post.analytics_summary || {};
    const claims = post.details?.coupon_enabled ? post.coupon_claims_count : null;
    const c = useCountdown(post.valid_to);
    const showRenew =
        post.status === "PUBLISHED" &&
        post.type === "OFFER" &&
        !c.expired &&
        c.isEndingSoon;
    const [renewOpen, setRenewOpen] = useState(false);

    return (
        <div className="relative" data-testid={`offer-card-${post.id}`}>
            <Link
                to={`/merchant/offers/${post.id}`}
                data-testid={`offer-row-${post.id}`}
                className="flex items-stretch gap-3 rounded-2xl border border-neutral-200 bg-white p-3 hover:bg-neutral-50"
            >
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-neutral-100">
                    {post.poster_image_url ? (
                        <img
                            src={publicAssetUrl(post.poster_image_url)}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    ) : null}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="truncate text-sm font-bold text-neutral-800">
                                    {post.title}
                                </div>
                                {post.status === "PUBLISHED" && (
                                    <EndingSoonBadge
                                        validTo={post.valid_to}
                                        testid={`merchant-offer-ending-soon-${post.id}`}
                                    />
                                )}
                                {showRenew && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setRenewOpen(true);
                                        }}
                                        data-testid={`merchant-offer-renew-btn-${post.id}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-orange-500 bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-600 shadow-sm transition hover:bg-orange-50"
                                    >
                                        <RefreshCw size={11} strokeWidth={2.5} /> Renew
                                    </button>
                                )}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-neutral-500">
                                {post.details?.discount_percent
                                    ? `${post.details.discount_percent}% off · `
                                    : ""}
                                {post.district_slug}
                            </div>
                        </div>
                        <Badge
                            variant="outline"
                            className={`shrink-0 text-[10px] uppercase ${STATUS_TONE[post.status]}`}
                        >
                            {post.status}
                        </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-neutral-600">
                        <span className="inline-flex items-center gap-1">
                            <Eye size={12} /> {a.views_total ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <MousePointerClick size={12} /> {a.engagements_total ?? 0}
                        </span>
                        {claims !== null && (
                            <span className="inline-flex items-center gap-1">
                                <TicketPercent size={12} /> {claims} claimed
                            </span>
                        )}
                    </div>
                </div>
                <ChevronRight size={16} className="self-center text-neutral-400" />
            </Link>
            {post.status === "PUBLISHED" && post.type === "OFFER" && (
                <div
                    className="absolute bottom-2 right-2"
                    onClick={(e) => e.preventDefault()}
                    role="presentation"
                >
                    <BoostButton post={post} size="sm" />
                </div>
            )}
            <RenewDialog
                open={renewOpen}
                onOpenChange={setRenewOpen}
                post={post}
                onSaved={(updated) => {
                    setRenewOpen(false);
                    onRenewed?.(updated);
                }}
            />
        </div>
    );
}

function EmptyOffers() {
    return (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
            <div className="text-sm font-semibold text-neutral-700">
                No offers yet
            </div>
            <div className="mt-1 text-xs text-neutral-500">
                Publish your first offer and start driving footfall.
            </div>
            <Link to="/merchant/offers/new">
                <Button className="mt-4 nest-gradient text-white" data-testid="offers-empty-create-btn">
                    <Plus size={16} className="mr-2" /> Create offer
                </Button>
            </Link>
        </div>
    );
}


/**
 * Renew dialog — merchant reviews and confirms a new `valid_to` for an
 * expiring offer.
 *
 * Pre-shift rule (per spec):
 *   - existing valid_to in the future → pre-fill `valid_to + 14 days`
 *   - existing valid_to in the past or missing → pre-fill `now + 14 days`
 *
 * Save path: PATCH /api/posts/{id} with `{ valid_to: iso }` — the same
 * endpoint used everywhere else for post updates. No new endpoint added.
 */
function RenewDialog({ open, onOpenChange, post, onSaved }) {
    const computeDefault = () => {
        const now = new Date();
        const cur = post?.valid_to ? new Date(post.valid_to) : null;
        const base = cur && !Number.isNaN(cur.getTime()) && cur.getTime() > now.getTime() ? cur : now;
        const next = new Date(base.getTime() + 14 * 24 * 3600 * 1000);
        return next;
    };
    // datetime-local requires a YYYY-MM-DDTHH:mm string (local time, no tz suffix).
    const toLocalInput = (d) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    // Recompute the pre-fill every time the dialog opens so a merchant that
    // opens → cancels → reopens still sees an up-to-date default.
    useEffect(() => {
        if (open) setValue(toLocalInput(computeDefault()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, post?.valid_to]);

    const currentLabel = post?.valid_to
        ? new Date(post.valid_to).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
        : "—";

    const submit = async () => {
        if (!value) return;
        const newDate = new Date(value);
        if (Number.isNaN(newDate.getTime())) {
            toast.error("Pick a valid date & time.");
            return;
        }
        if (newDate.getTime() <= Date.now()) {
            toast.error("New expiry must be in the future.");
            return;
        }
        try {
            setSaving(true);
            const res = await api.patch(`/posts/${post.id}`, { valid_to: newDate.toISOString() });
            toast.success("Offer renewed.");
            onSaved?.(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={`merchant-offer-renew-dialog-${post.id}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw size={16} className="text-orange-600" /> Renew offer
                    </DialogTitle>
                    <DialogDescription>
                        Extends this offer's expiry. Pre-filled to 14 days after the current
                        expiry — adjust if you need a different date.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Currently valid until</Label>
                        <div
                            className="mt-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700"
                            data-testid={`merchant-offer-renew-current-${post.id}`}
                        >
                            {currentLabel}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs" htmlFor={`renew-valid-to-${post.id}`}>
                            New expiry date &amp; time
                        </Label>
                        <Input
                            id={`renew-valid-to-${post.id}`}
                            type="datetime-local"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            data-testid={`merchant-offer-renew-input-${post.id}`}
                            className="mt-1"
                        />
                        <p className="mt-1 text-[11px] text-neutral-500">
                            Default: current expiry + 14 days.
                        </p>
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                        data-testid={`merchant-offer-renew-cancel-${post.id}`}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={saving || !value}
                        className="nest-gradient text-white"
                        data-testid={`merchant-offer-renew-save-${post.id}`}
                    >
                        {saving ? (
                            <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</>
                        ) : (
                            <><RefreshCw size={14} className="mr-1" /> Renew offer</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
